import {
  parseServerMessage,
  ROOM_PASSWORD_REQUIRED,
  ROOM_PASSWORD_SLOW,
  ROOM_PASSWORD_WRONG,
  WS_HEARTBEAT,
  type ClientMessage,
  type ModerationAction,
  type ServerMessage,
} from "@yay/shared";
import { capacityCheckUrl, config } from "../config";

type MessageHandler = (msg: ServerMessage) => void;
type StatusHandler = (status: ConnectionStatus) => void;
type ReadyHandler = (ready: boolean) => void;

export type ConnectionStatus = "idle" | "connecting" | "connected" | "disconnected";

const PING_INTERVAL = 15_000;
const WATCHDOG_INTERVAL = 5_000;
/**
 * DO-visible liveness heartbeat cadence. The 15s PING is auto-answered by
 * the DO runtime (never wakes the Durable Object — keeps edge/NAT state
 * alive for free), so the server can't see it. ALIVE is a separate message
 * that DOES reach the DO: it's what lets the server reap truly dead tabs
 * (laptop off, network drop — no close frame ever arrives) instead of
 * letting their avatars linger for days.
 */
const ALIVE_INTERVAL = 60_000;
// How long without any received message before the watchdog considers
// the connection dead. Deliberately generous (2 min) — pings go out
// every 15s so a healthy connection should see pong activity well
// within this window. We also skip the dead check when the tab is
// hidden (see startPing), so this is purely a safety net for visible tabs.
const DEAD_TIMEOUT = 120_000;
/** After this much silence, a "still open" socket is treated as suspect on wake. */
const STALE_ON_WAKE_MS = 12_000;

/**
 * Production WebSocket client with exponential backoff reconnect,
 * watchdog dead-connection detection, and tab-visibility awareness.
 */
/** Messages the caller can enqueue. connectionId is filled in at flush time. */
type OutboundMessage =
  | { type: "join"; username: string }
  | { type: "chat"; text: string; replyToId?: string }
  | { type: "rename"; username: string }
  | { type: "reroll"; avatarSeed?: string }
  | { type: "react"; messageId: string; emoji: string }
  | { type: "set_mode"; mode: "watch" | "nerd" }
  | { type: "flash_add"; term: string; definition: string }
  | {
      type: "flash_import";
      cards: Array<{ term: string; definition: string }>;
    }
  | { type: "flash_update"; id: string; term: string; definition: string }
  | { type: "flash_delete"; id: string }
  | { type: "flash_nav"; index?: number; delta?: number }
  | { type: "flash_flip" }
  | { type: "flash_follow"; targetUserId: string | null }
  | { type: "flash_lock"; locked: boolean }
  | { type: "set_media"; youtube: string }
  | { type: "media_sync"; playing?: boolean; positionSec?: number }
  | { type: "report"; targetUserId: string; reason: string }
  | { type: "command"; action: ModerationAction; targetIds: string[] }
  | { type: "whisper"; targetUserId: string; text: string }
  | { type: "poll_create"; title: string; options: string[] }
  | { type: "poll_vote"; pollId: string; optionIndex: number }
  | { type: "poll_close"; pollId: string }
  | { type: "vote_cast"; voteId: string; approve: boolean }
  | { type: "set_password"; password: string };

export class YayConnection {
  private ws: WebSocket | null = null;
  private reconnectDelay = 1_000;
  private readonly maxReconnectDelay = 30_000;
  private shouldReconnect = true;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private handlers = new Set<MessageHandler>();
  private statusHandlers = new Set<StatusHandler>();
  private readyHandlers = new Set<ReadyHandler>();
  private kickedHandlers = new Set<(reason: string) => void>();
  private passwordFailHandlers = new Set<(reason: string) => void>();
  /** Set once the server kicks/bans this session — terminal, no auto-rejoin. */
  private kickedReason: string | null = null;
  private capacityHandlers = new Set<() => void>();
  /**
   * Server is at capacity / under maintenance (cost gate): the room is
   * closed. Terminal like kicked — the branded screen replaces the app and
   * the reconnect loop stops until the user retries.
   */
  private atCapacity = false;
  private _connectionId: string | null = null;
  private _status: ConnectionStatus = "idle";
  private lastActivityTime = Date.now();
  private lastPingTime = 0;
  private lastAliveTime = 0;
  /**
   * After the first confirmed identity, further socket handshakes are
   * reconnects (`rejoin: true`) so the room doesn't re-announce us.
   * sessionStorage covers reload / discard restore (new JS heap, same tab).
   */
  private everIdentified = false;
  /** Chrome restored this document after Memory Saver discarded it. */
  private readonly wasDiscarded =
    typeof document !== "undefined" &&
    (document as Document & { wasDiscarded?: boolean }).wasDiscarded === true;
  /** True after the Page Lifecycle `freeze` event until `resume`. */
  private frozen = false;
  private onVisibilityChange: (() => void) | null = null;
  private onFreeze: (() => void) | null = null;
  private onResume: (() => void) | null = null;
  private onPageHide: ((event: PageTransitionEvent) => void) | null = null;
  /**
   * Buffer of user-intent messages that couldn't be sent yet (socket down, or
   * identity not yet confirmed after a reconnect). Flushed in order once the
   * server confirms our session via `identity`. This is why chat/renames/avatar
   * changes are never silently dropped across reconnects.
   */
  private outbox: OutboundMessage[] = [];
  /**
   * Messages written to the socket since the last server confirmation. On
   * "session expired" these are moved back to the outbox and resent after rejoin.
   */
  private inFlight: OutboundMessage[] = [];
  /** True once the server has (re)confirmed our identity on the live socket. */
  private ready = false;

  /**
   * Intents safe to re-send after a reconnect: idempotent (replaying them
   * lands in the same state) or last-write-wins. Everything else — chat,
   * whispers, commands, polls, reports, reactions, deck adds, media
   * syncs — is a ONE-SHOT intent: replaying it duplicates or teleports
   * (a whisper re-sent on every rejoin = N whisper bubbles AND N D1 rows).
   * One-shots are dropped on reconnect, exactly like chat is never re-sent.
   *
   * NOTE (Aug 2026): media_sync / set_media are deliberately NOT here. They
   * LOOK last-write-wins, but the server re-bases the shared playhead's
   * wall-clock anchor (`at = now`) on every sync, so replaying a
   * minutes-old sync teleports every viewer to that stale position (the
   * "video finished/paused/jumped randomly, resume from 0" bug). The
   * reconnect's room_state resync re-applies the server's ACTUAL anchor,
   * so dropping them on replay loses nothing. The session-expired
   * re-queue path below still re-sends them — those were rejected by the
   * server, so they're fresh, not stale.
   */
  private static readonly IDEMPOTENT_INTENTS = new Set([
    "rename",
    "reroll",
    "set_mode",
    "flash_nav",
    "flash_flip",
    "flash_lock",
    "flash_follow",
    "flash_update",
    "flash_delete",
  ]);

  private onWake: (() => void) | null = null;
  /** Cooldown to debounce rapid wake events (visibilitychange + focus + online). */
  private lastWakeMs = 0;
  private static readonly WAKE_COOLDOWN_MS = 3_000;
  /** Cooldown to prevent forceReconnect stacking from watchdog + wake + stale onclose. */
  private lastForceReconnectMs = 0;
  private static readonly FORCE_RECONNECT_COOLDOWN_MS = 15_000;

  constructor(
    private readonly url: string,
    private readonly sessionId: string,
    private username: string,
    /** Persisted visual identity, replayed on join for silent resume. */
    private identity: {
      userId?: string;
      username?: string;
      color?: string;
      avatarSeed?: string;
    } = {},
    private roomPassword: string | null = null,
  ) {
    if (!this.identity.username) {
      this.identity.username = username;
    }
  }

  /**
   * Prefer the last server-confirmed name so a reconnect/wake doesn't
   * accidentally overwrite a rename that arrived from another tab.
   */
  private get confirmedUsername(): string {
    return this.identity.username || this.username;
  }

  /** Keep the re-join username in sync after a rename. */
  setUsername(username: string): void {
    this.username = username;
    this.identity = { ...this.identity, username };
  }

  /** Keep the re-join identity in sync after a reroll/rename/cross-tab update. */
  setIdentity(identity: {
    userId?: string;
    username?: string;
    color?: string;
    avatarSeed?: string;
  }): void {
    this.identity = { ...this.identity, ...identity };
    if (identity.username) {
      this.username = identity.username;
    }
  }

  get connectionId(): string | null {
    return this._connectionId;
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  /** Whether the server has confirmed our session (safe to expect chat/profile acks). */
  get isReady(): boolean {
    return this.ready;
  }

  onMessage(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  onReady(handler: ReadyHandler): () => void {
    this.readyHandlers.add(handler);
    handler(this.ready);
    return () => this.readyHandlers.delete(handler);
  }

  /** Reason this connection was kicked/banned, or null (terminal state). */
  get kicked(): string | null {
    return this.kickedReason;
  }

  onKicked(handler: (reason: string) => void): () => void {
    this.kickedHandlers.add(handler);
    return () => this.kickedHandlers.delete(handler);
  }

  onPasswordFail(handler: (reason: string) => void): () => void {
    this.passwordFailHandlers.add(handler);
    return () => this.passwordFailHandlers.delete(handler);
  }

  /** True once the server said it's at capacity (or maintenance is on). */
  get capacity(): boolean {
    return this.atCapacity;
  }

  onCapacity(handler: () => void): () => void {
    this.capacityHandlers.add(handler);
    return () => this.capacityHandlers.delete(handler);
  }

  /**
   * Terminal capacity state: stop the reconnect loop and tell the UI to
   * show the branded screen. Idempotent — the preflight, the server's
   * `capacity` message, and the 1013 close can all fire for one event.
   */
  private setCapacity(): void {
    if (this.atCapacity) return;
    this.atCapacity = true;
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    this.setReady(false);
    for (const handler of this.capacityHandlers) {
      handler();
    }
  }

  /**
   * Cost-gate preflight: if the server is at capacity / under maintenance,
   * don't even open the socket — the UI shows the branded screen instead of
   * a silent reconnect loop. Any network hiccup falls through to the normal
   * connect path (the reconnect logic already handles outages).
   */
  private async isAtCapacity(): Promise<boolean> {
    try {
      const res = await fetch(capacityCheckUrl());
      if (!res.ok) return false;
      const body = (await res.json()) as { state?: string };
      return body.state !== undefined && body.state !== "ok";
    } catch {
      return false;
    }
  }

  private setReady(ready: boolean): void {
    if (this.ready === ready) return;
    this.ready = ready;
    for (const handler of this.readyHandlers) {
      handler(ready);
    }
  }

  connect(): void {
    this.shouldReconnect = true;
    this.clearReconnectTimer();

    // Coming back to the tab, or the network returning, should reconnect
    // *immediately* — never wait out a backoff timer. This is what makes
    // returning from hours of idle feel instant, Twitch-style.
    if (typeof document !== "undefined") {
      this.onVisibilityChange = () => {
        if (document.visibilityState === "visible") this.wake();
      };
      document.addEventListener("visibilitychange", this.onVisibilityChange);
      this.onFreeze = () => {
        this.frozen = true;
        this.sendPark();
      };
      this.onResume = () => {
        this.frozen = false;
        this.wake();
      };
      document.addEventListener("freeze", this.onFreeze);
      document.addEventListener("resume", this.onResume);
    }
    if (typeof window !== "undefined") {
      this.onWake = () => this.wake();
      window.addEventListener("online", this.onWake);
      window.addEventListener("focus", this.onWake);
      this.onPageHide = (event: PageTransitionEvent) => {
        this.handlePageHide(event);
      };
      window.addEventListener("pagehide", this.onPageHide);
    }

    this.open();
  }

  /** Explicit in-app leave (button): announce immediately, then tear down. */
  leave(): void {
    clearTabIdentified();
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(
          JSON.stringify({
            type: "leave",
            connectionId: this._connectionId ?? "",
            sessionId: this.sessionId,
          }),
        );
      } catch {
        /* fall through to disconnect */
      }
    }
    this.disconnect();
  }

  /**
   * Silent "I'm still in the strip, JS is asleep." Socket close after this
   * does not announce "left". Used on freeze, bfcache, discard, and mobile
   * backgrounding — not on a desktop tab close (no freeze).
   */
  private sendPark(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(
        JSON.stringify({
          type: "park",
          connectionId: this._connectionId ?? "",
          sessionId: this.sessionId,
        }),
      );
    } catch {
      /* closing */
    }
  }

  private handlePageHide(event: PageTransitionEvent): void {
    if (event.persisted) {
      this.sendPark();
      return;
    }
    // Mobile lock / app switch fires pagehide with persisted=false. Park so
    // the close is silent. Desktop tab close has no freeze and is not mobile
    // — send nothing; socket close + presence grace announces "left".
    if (this.frozen || isMobileClient()) {
      this.sendPark();
    }
  }

  /** Same tab (reload / discard / this connection) should not re-announce join. */
  private shouldRejoin(): boolean {
    return this.everIdentified || this.wasDiscarded || tabWasIdentified();
  }

  private joinPayload(rejoin = false): {
    type: "join";
    username: string;
    color?: string;
    avatarSeed?: string;
    userId?: string;
    rejoin?: boolean;
    password?: string;
  } {
    return {
      type: "join",
      username: this.confirmedUsername,
      color: this.identity.color,
      avatarSeed: this.identity.avatarSeed,
      userId: this.identity.userId,
      ...(rejoin ? { rejoin: true } : {}),
      ...(this.roomPassword ? { password: this.roomPassword } : {}),
    };
  }

  /**
   * Force an immediate (re)connect if the socket isn't healthy. On wake with an
   * apparently-open but quiet socket, re-assert join so a GC'd session is rebuilt
   * before the user tries chat/avatar again.
   */
  private wake(): void {
    if (!this.shouldReconnect) return;

    const now = Date.now();
    if (now - this.lastWakeMs < YayConnection.WAKE_COOLDOWN_MS) return;
    this.lastWakeMs = now;

    if (
      this.ws &&
      this.ws.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const silentFor = Date.now() - this.lastActivityTime;
      if (silentFor > DEAD_TIMEOUT) {
        this.forceReconnect();
        return;
      }
      if (silentFor > STALE_ON_WAKE_MS || !this.ready) {
        // Re-join on a live socket: heals "session gone, TCP still up" without
        // tearing down the connection. Identity reply re-enables the outbox.
        this.setReady(false);
        this.transmit(this.joinPayload(true));
        return;
      }
      this.sendHeartbeat();
      return;
    }

    this.reconnectDelay = 1_000;
    this.clearReconnectTimer();
    this.open();
  }

  /** Re-join after the server says our session is gone (socket may still be up). */
  rejoin(): void {
    if (!this.shouldReconnect) return;
    this.setReady(false);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.transmit(this.joinPayload(true));
      return;
    }
    this.reconnectDelay = 1_000;
    this.clearReconnectTimer();
    this.forceReconnect();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.setReady(false);
    this.outbox = [];
    this.inFlight = [];
    this.clearReconnectTimer();
    this.clearPingTimer();

    if (this.onVisibilityChange && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
      this.onVisibilityChange = null;
    }
    if (this.onFreeze && typeof document !== "undefined") {
      document.removeEventListener("freeze", this.onFreeze);
      this.onFreeze = null;
    }
    if (this.onResume && typeof document !== "undefined") {
      document.removeEventListener("resume", this.onResume);
      this.onResume = null;
    }
    if (this.onWake && typeof window !== "undefined") {
      window.removeEventListener("online", this.onWake);
      window.removeEventListener("focus", this.onWake);
      this.onWake = null;
    }
    if (this.onPageHide && typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.onPageHide);
      this.onPageHide = null;
    }

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
    this._connectionId = null;
    this.setStatus("idle");
  }

  /**
   * Enqueue a user-intent message. It's sent immediately if the session is
   * live, otherwise buffered and flushed once identity is (re)confirmed.
   * Always "succeeds" from the caller's view — nothing is dropped.
   */
  send(data: OutboundMessage): boolean {
    // Coalesce rapid YouTube seeks — only the latest playhead matters.
    if (data.type === "media_sync") {
      this.outbox = this.outbox.filter((m) => m.type !== "media_sync");
    }
    this.outbox.push(data);
    this.flush();
    return true;
  }

  private flush(): void {
    if (!this.isReady || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    const pending = this.outbox;
    this.outbox = [];
    for (let i = 0; i < pending.length; i++) {
      const msg = pending[i]!;
      if (!this.transmit(msg)) {
        // Socket died mid-flush — put the unsent remainder back, in order.
        this.outbox = pending.slice(i).concat(this.outbox);
        return;
      }
      if (msg.type !== "join") {
        // Coalesce media_sync in the replay queue too (mirrors the outbox
        // coalescing in send()): only the LATEST playhead matters, so a
        // reconnect replay or session-expired re-queue can never fire a
        // burst of old positions.
        if (msg.type === "media_sync") {
          this.inFlight = this.inFlight.filter((m) => m.type !== "media_sync");
        }
        this.inFlight.push(msg);
      }
    }
  }

  private transmit(
    data:
      | OutboundMessage
      | {
          type: "join";
          username: string;
          color?: string;
          avatarSeed?: string;
          userId?: string;
          rejoin?: boolean;
          password?: string;
        },
  ): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;

    const payload = {
      ...data,
      connectionId: this._connectionId ?? "",
      sessionId: this.sessionId,
    } as ClientMessage;

    // NOTE: do NOT touch lastActivityTime here. It must track only *received*
    // activity, otherwise our own outgoing pings would mask a half-open socket
    // (server gone, but our TCP close hasn't fired) and the watchdog below
    // would never trigger a reconnect.
    this.ws.send(JSON.stringify(payload));
    return true;
  }

  /**
   * Exact-string heartbeat for Durable Object setWebSocketAutoResponse.
   * Must not include connectionId/sessionId or the runtime won't auto-reply
   * and the DO would wake on every ping.
   */
  private sendHeartbeat(): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(WS_HEARTBEAT.PING);
    return true;
  }

  /**
   * DO-visible liveness heartbeat (WS_HEARTBEAT.ALIVE — exact string, no
   * connectionId/sessionId). Unlike PING this wakes the DO, which stamps
   * the socket's last-seen; sockets silent past the server's idle window
   * are reaped as zombies. This is the signal that makes dead tabs
   * disappear without needing a close frame.
   */
  private sendAlive(): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(WS_HEARTBEAT.ALIVE);
    return true;
  }

  private open(): void {
    this.setReady(false);
    this.setStatus("connecting");

    // Drop a defunct socket before opening a new one (wake / reconnect paths).
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }

    // Cost-gate preflight runs first; the socket is only opened if the
    // server is accepting (state "ok"). At capacity → branded screen.
    void this.openSocket();
  }

  private async openSocket(): Promise<void> {
    if (await this.isAtCapacity()) {
      this.setCapacity();
      return;
    }

    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectDelay = 1_000;
      this.lastActivityTime = Date.now();
      this.startPing();
    };

    this.ws.onmessage = (event) => {
      this.lastActivityTime = Date.now();

      if (typeof event.data !== "string") return;
      const msg = parseServerMessage(event.data);
      if (!msg) return;

      if (msg.type === "connected") {
        this._connectionId = msg.connectionId;
        this.setStatus("connected");
        // Handshake: (re)join with our stable identity right away, including
        // persisted color/avatar so the server can silently rebuild us.
        this.transmit(this.joinPayload(this.shouldRejoin()));
      }

      // Server confirmed our session — safe to flush buffered messages.
      if (msg.type === "identity") {
        this.identity = {
          ...this.identity,
          userId: msg.user.id,
          username: msg.user.username,
          color: msg.user.color,
          avatarSeed: msg.user.avatarSeed,
        };
        this.username = msg.user.username;
        this.everIdentified = true;
        markTabIdentified();
        // Re-queue idempotent intents (rename/reroll/media) that were in
        // flight when the socket dropped — they're safe to replay. Chat and
        // one-shot intents (whisper/command/poll/report/react) are NOT
        // re-sent: replaying them duplicates the effect.
        const retry = this.inFlight.filter((m) =>
          YayConnection.IDEMPOTENT_INTENTS.has(m.type),
        );
        this.inFlight = [];
        if (retry.length > 0) {
          this.outbox = [...retry, ...this.outbox];
        }
        this.setReady(true);
        this.flush();
      }

      // Application-level acks for *our* intents (not every room broadcast).
      if (msg.type === "chat") {
        const idx = this.inFlight.findIndex(
          (m) => m.type === "chat" && m.text === msg.text,
        );
        if (idx >= 0) this.inFlight.splice(idx, 1);
      }
      if (msg.type === "user_updated") {
        this.identity = {
          ...this.identity,
          userId: msg.user.id,
          username: msg.user.username,
          color: msg.user.color,
          avatarSeed: msg.user.avatarSeed,
        };
        this.username = msg.user.username;
        this.inFlight = this.inFlight.filter((m) => m.type === "chat");
      }
      // A report is a one-shot intent — the ack is its final word. Without
      // this, the report lingered in inFlight and got re-sent on every
      // rejoin/identity, producing duplicate reports (and gate "Slow down"s).
      if (msg.type === "report_ack") {
        this.inFlight = this.inFlight.filter((m) => m.type !== "report");
      }

      // Server is at capacity / under maintenance (cost gate): terminal —
      // show the branded screen and stop the reconnect loop. The server
      // follows this message with a 1013 close.
      if (msg.type === "capacity") {
        this.setCapacity();
        return;
      }

      // Terminal moderation errors: being kicked/banned must stop the client's
      // self-heal machinery. Without this, the presence-mismatch rejoin (and
      // the session-expired rejoin on the next message) resurrect the removed
      // session within seconds — the "kick didn't stick" bug. Drop to the join
      // overlay instead; a manual re-join is always allowed (kicked ≠ banned).
      if (msg.type === "error" && typeof msg.text === "string") {
        const lower = msg.text.toLowerCase();
        if (lower.includes("kicked") || lower.includes("banned")) {
          this.kickedReason = msg.text;
          clearTabIdentified();
          this.disconnect();
          for (const h of this.kickedHandlers) h(msg.text);
          return;
        }
        if (
          msg.text === ROOM_PASSWORD_WRONG ||
          msg.text === ROOM_PASSWORD_REQUIRED ||
          msg.text === ROOM_PASSWORD_SLOW
        ) {
          this.disconnect();
          for (const h of this.passwordFailHandlers) h(msg.text);
          return;
        }
      }

      // Session was GC'd or wiped while the socket stayed up — rejoin and
      // resend any intents that failed because of it. Don't surface the raw
      // error when we can self-heal.
      if (
        msg.type === "error" &&
        typeof msg.text === "string" &&
        msg.text.toLowerCase().includes("session expired")
      ) {
        if (this.inFlight.length > 0) {
          // The server explicitly rejected these (it doesn't know this
          // session — DO hibernation/restart edge), so NOTHING was
          // processed. Re-sending is therefore safe and correct: the
          // rejected messages never landed, so there's no duplicate risk.
          // (One-shot intents are only dropped on the identity replay after
          // a NORMAL reconnect, where they may have landed.)
          this.outbox = this.inFlight.concat(this.outbox);
          this.inFlight = [];
        }
        this.rejoin();
        return;
      }

      for (const handler of this.handlers) {
        try {
          handler(msg);
        } catch (err) {
          console.error("[yay] message handler error", msg.type, err);
        }
      }
    };

    this.ws.onclose = (event) => {
      this._connectionId = null;
      this.setReady(false);
      this.clearPingTimer();
      this.setStatus("disconnected");
      // 1013 = "try again later" — the server closed us for capacity.
      // (Normally the `capacity` message arrives first; this is the
      // belt-and-suspenders path if it was missed.)
      if (event.code === 1013) {
        this.setCapacity();
        return;
      }
      if (this.shouldReconnect) this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      // onclose follows
    };
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.open();
    }, this.reconnectDelay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startPing(): void {
    this.clearPingTimer();
    this.lastPingTime = Date.now();
    this.lastAliveTime = Date.now();
    this.pingTimer = setInterval(() => {
      // When the tab is hidden, skip the dead-connection check — browser timer
      // throttling can delay pong processing, and we don't want to force-reconnect
      // just because the user stepped away. The wake() path handles reconnection
      // when they return.
      const isHidden =
        typeof document !== "undefined" && document.visibilityState === "hidden";

      if (!isHidden && Date.now() - this.lastActivityTime > DEAD_TIMEOUT) {
        this.forceReconnect();
        return;
      }

      if (Date.now() - this.lastPingTime >= PING_INTERVAL) {
        const ok = this.sendHeartbeat();
        if (!ok) {
          this.forceReconnect();
          return;
        }
        this.lastPingTime = Date.now();
      }

      // DO-visible liveness heartbeat — keeps running while hidden so a
      // backgrounded (mobile) tab is never reaped as a zombie.
      if (Date.now() - this.lastAliveTime >= ALIVE_INTERVAL) {
        const ok = this.sendAlive();
        if (!ok) {
          this.forceReconnect();
          return;
        }
        this.lastAliveTime = Date.now();
      }
    }, WATCHDOG_INTERVAL);
  }

  private clearPingTimer(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private forceReconnect(): void {
    const now = Date.now();
    if (now - this.lastForceReconnectMs < YayConnection.FORCE_RECONNECT_COOLDOWN_MS) return;
    this.lastForceReconnectMs = now;

    this.setReady(false);
    this.clearPingTimer();
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
    this._connectionId = null;
    this.setStatus("disconnected");
    if (this.shouldReconnect) this.scheduleReconnect();
  }

  private setStatus(status: ConnectionStatus): void {
    this._status = status;
    for (const handler of this.statusHandlers) {
      handler(status);
    }
  }
}

function tabWasIdentified(): boolean {
  try {
    return sessionStorage.getItem(config.tabIdentifiedKey) === "1";
  } catch {
    return false;
  }
}

function markTabIdentified(): void {
  try {
    sessionStorage.setItem(config.tabIdentifiedKey, "1");
  } catch {
    /* private mode / quota */
  }
}

function clearTabIdentified(): void {
  try {
    sessionStorage.removeItem(config.tabIdentifiedKey);
  } catch {
    /* ignore */
  }
}

function isMobileClient(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
